import {type RecordOf, Map, Set, OrderedSet, List} from 'immutable';
import type {
  BreakpointID,
  BreakpointsMap,
  CssPropertyMap,
} from '@packages/systems/style/types';
import {type StyleBlock} from '@packages/systems/style-legacy/records';
import type {
  CustomNodeStateKey,
  RawStyleBlockServerType,
  PseudoStateKey,
} from '@packages/systems/core/utils/StyleTypes';
import {Breakpoint} from '@packages/systems/style/media-queries';
import {Component, Plugins} from '@packages/systems/wfdl';
import {
  VariantDataMap,
  VariantStateKey,
} from '@packages/systems/style-legacy/selectors';
import {
  LibrariesCapabilitiesMap,
  type InstalledLibraryTagStylesSettings,
} from '@packages/systems/libraries';
import type {IX3State} from '@packages/systems/ix3/store';

export type ChangedRuleShape = {
  /* The guid (e.g. "block-123:main_hover") of the StyleRule that was changed via updateStyleRule */
  guid: null | string | Array<string>;
  /* Indicates that the last CSS styleLess update should only be written to CSSOM but should not persist to the server */
  ephemeral: boolean;
  /* If a change is ephemeral, keeps the map of old CSS prop+values to restore to */
  oldValues: Map<string, string>;
};

export type ChangedRuleType = RecordOf<ChangedRuleShape>;
export type DecoratableWithMaps =
  | {
      decorate?: false;
      slugMap?: Plugins.PluginsType['slugMap'];
      variantDataMap?: VariantDataMap;
    }
  | {
      decorate: true;
      slugMap: Plugins.PluginsType['slugMap'];
      variantDataMap: VariantDataMap;
    };

/**
 * A prefix used on keys of the affectsMap to indicate that the key is a
 * component ID, rather than a page ID
 */
export const COMPONENT_AFFECTS_PREFIX = 'component:' as const;

export type StyleBlockStateShape = {
  /** List of media query breakpoints that will allow larger media queries */
  breakpoints: BreakpointsMap<Breakpoint>;
  /** A guid -> StyleBlockRecord map that is the primary source of truth for the styles that make up the site */
  styleBlocks: Map<string, StyleBlock>;
  /**
   * A guid -> Map((pageId | "component:"+componentId) => count) map which records how many times each StyleBlock is used on each page
   * We keed this info outside of styleBlocks to make it easier to detect when block changes require a CSSOM sync
   *
   * NOTE: `affectsMap` only contains the counts from the server; these are not necessarily up-to-date with the
   * current state of the current page.
   *
   * Therefore, DO NOT rely on `affectsMap` to get the count for the current page; for that you want to use
   * one of the following from `AffectedNodesUtils`:
   *
   * - `AffectedNodesUtils.calculateAffectedForStyleBlock`
   * - `AffectedNodesUtils.recalculateAffected` (avoid this if possible; it can be very costly)
   */
  affectsMap: Map<
    // The `string | ...` eats the format of `component:<ComponentName>` but we
    // specify it here in the typedef for clarity of intent:
    string | `${typeof COMPONENT_AFFECTS_PREFIX}${Component.ComponentName}`,
    Map<string, number>
  >;
  /** A child_guid -> parent_guid to enable walking up the style tree */
  parentIndex: Map<string, string>;
  /**
   * A list of guids representing the order of the root/non-nested blocks
   * Nested StyleBlocks are ordered by each record's children array
   */
  globalOrder: OrderedSet<string>;
  /**
   * Contains info about the last already-existing single StyleRule that had an atomic CSS change
   * This is used to instruct StyleSheetUtils.syncStyleSheet to take a shortcut and not re-compare the entire st
   */
  changedRule: ChangedRuleType;
  /**
   * The guid (e.g. 'block-123') that was just created, to signal to CSSOM sand others to avoid certain unnecessary costly operations
   */
  _hack_addedRuleGuid: null | string;
  /**
   * A simple key -> Boolean map to keep track of which migrations have been run on the current site's style blocks
   * This is mostly used to pass through values from server-side JSON data back to CssSync
   */
  migrations: Map<string, boolean>;
  /**
   * A guid -> Map(cssProperty => Boolean) map to keep track of which CSS properties apply to a specific
   * style block id. Used for canvas related features, like calculating if the empty dotted decorator has to be applied
   */
  cssPropertyMap: CssPropertyMap;
  /**
   * A set of style block IDs to keep track of which style blocks belong to the site or page branch
   * This changes when loading a page from a page branch and vice-versa
   */
  styleBlockIds: Set<string>;
  /**
   * A set of style block IDs to keep track of which styleBlocks belong exclusively to the site, not a page branch
   * This changes when loading a page from a page branch and vice-versa
   * When loading a page in the main site, this will be empty; when loading a page branch, this will contain the IDs for main site style blocks
   */
  baseStyleBlockIds: Set<string>;
  /**
   * Indicates which branch's data is loaded into the style store. If null, main site styles are loaded. Since store
   * updates are not atomic, this only indicates that the StyleStore has data from this branch loaded and is used to
   * ensure we are not executing logic cross-stores while branch data is still loading.
   */
  branchId: string | null;
  /**
   * The site's tag styles settings for its installed libraries, joined with
   * their priority order.
   */
  installedLibrariesTagStylesSettings: InstalledLibraryTagStylesSettings;
  /**
   * A copy of the StyleBlockState when asset-hover previewing began, as an easy
   * restore point.
   *
   * Something like this could be helpful in replacing action-sequences like this:
   * - startSetStyle (punch in)
   * - setStyle(s) (set some ephemeral styles [font family, bg img, etc])
   * - endSetStyle (punch out, with or without "commit")
   *
   * Tagged this as __HACK__ mainly to highlight that it hasn't been generalized yet.
   */
  __HACK__assetHoverRestorePoint: null | StyleBlockStateType;
};

export interface StyleBlockStateMethods {
  /**
   * When an action that may affect multiple CSSOM rules takes place,
   * need to make sure to clear single block metadata to avoid short-curcuiting stylesheet sync
   */
  resetChangedRule(): StyleBlockStateType;
  resetAddedRule(): StyleBlockStateType;
  /** Gets all other media queries that come before this one */
  getEffectiveBreakpointIds(thisQueryKey: BreakpointID): Array<BreakpointID>;
  getOrderedBreakpointIds(): Array<BreakpointID>;
  /**
   * Grabs a StyleBlock, but decorates it with selector data.
   * @param  {String}     guid
   * @param  {Boolean}    options.decorate  If true, will add selector data derived from the block's tree location.
   * @return {StyleBlock}
   */
  getBlock(
    guid?: string | null,
    params?: DecoratableWithMaps & {
      skipVariants?: boolean;
    }
  ): StyleBlock | undefined;
  /**
   * Returns the recursive closure of all styleBlock ids in the subtree rooted at rootStyleBlockId, inclusive.
   * @param  {String} rootStyleBlockId       The root style block id for the subtree we're interested in. It should exist in the store.
   * @return {String}                        The resulting list of style block ids. No particular order guaranteed.
   */
  getBlockIdClosure(rootStyleBlockId: string): Array<StyleBlock['_id']>;
  /**
   * Returns the styleLess from a block or one of its variants.
   * @param  {String} options.styleBlockGuid The guid of the parent StyleBlock.
   * @param  {String} options.mediaQuery     The media query key of the variant, if applicable - e.g. 'tiny'
   * @param  {String} options.customNodeState   The custom node state of the variant, if applicable - e.g. 'current'
   * @param  {String} options.pseudoState    The hover state of the variant, if applicable - e.g. 'hover'
   * @return {String}                        The styleLess of the variant, or null if the variant or block doesn't exist.
   */
  getStyles(arg1: {
    styleBlockGuid: StyleBlock['_id'];
    mediaQuery?: BreakpointID;
    customNodeState?: null | CustomNodeStateKey;
    pseudoState?: null | PseudoStateKey;
    variantState?: null | VariantStateKey;
  }): null | string;
  /**
   * Gets the parent StyleBlock for a leaf.
   * @param  {String} childGuid
   * @return {StyleBlock}
   */
  getParent(childGuid: string): StyleBlock | null | undefined;
  /**
   * Gets the list of children for a StyleBlock.
   * @param  {String}       guid              The GUID of the parent StyleBlock.
   * @param  {Boolean}      options.decorate  If true, will add selector data derived from the block's tree location.
   * @return {StyleBlock[]}                   The block's children in order.
   */
  getChildren(
    guid: string,
    arg2?: {
      decorate?: boolean;
    }
  ): OrderedSet<StyleBlock>;
  /**
   * Creates a path of StyleBlocks that show the path from a root node to the leaf node.
   * @param  {String}       leafGuid  The leaf node to create the path to.
   * @return {StyleBlock[]}           The ordered path from a root StyleBlock to the leaf StyleBlock, including both.
   */
  getTreePath(leafGuid: string): Array<StyleBlock>;
  /**
   * Gets the root StyleBlock for any other StyleBlock. Walks up the style tree.
   * @param  {String}            leafGuid   The GUID of any leaf StyleBlock.
   * @return {StyleBlockRecord}             The root StyleBlock. It could be the same as the leafGuid if that matches a global StyleBlock.
   */
  getRoot(leafGuid?: string | null | undefined): null | StyleBlock;
  /**
   * Returns all top-level StyleBlocks (both classes and tags) in creation order.
   * @return {OrderedSet}
   */
  getGlobalStyleBlocks(): OrderedSet<StyleBlock>;
  /**
   * Returns only non-fake type="class" StyleBlocks that are in the global scope.
   * @return {Map}  Maps String values (the style id) to StyleBlock
   */
  getGlobalClassBlocksMap(): Map<string, StyleBlock>;
  /**
   * Returns the topologically sorted StyleBlock tree in a flat list.
   * @param  {String}             options.rootGuid      The root GUID at which we should start. If not defined, will start globally.
   * @param  {Boolean}            options.decorate      If true, will add selector data derived from the block's tree location.
   * @param  {Boolean}            options.includeFake   If true, will include non-reified default tag blocks marked with fake=true.
   * @return {StyleBlockRecord[]}                       The ordered list of StyleBlocks.
   */
  getOrderedList(
    params: DecoratableWithMaps & {
      rootGuid?: string;
      includeFake?: boolean;
    }
  ): Array<StyleBlock>;
  /**
   * Returns a topoloogically sorted StyleBlock tree in a flat list, limited to just the leaf block GUIDs passed in.
   * @param  {String[]}           options.leafGuids   A list of leaf blocks to include in the sorted list.
   * @param  {Boolean}            options.includeFake If true, non-reified tag blocks will be included.
   * @return {StyleBlockRecord[]}                     The sheet-ordered list of StyleBlocks, only includes leaf nodes, and all descendants of their root nodes.
   */
  getPartialOrderedList(params?: {
    leafGuids?: Array<string>;
    includeFake?: boolean;
  }): Array<StyleBlock>;
  /**
   * True when the block is a tag style whose library has its tag styles
   * switched off, and so contributes nothing to the rendered page.
   * @param  {String}  blockGuid  The StyleBlock guid. An unknown guid is not hidden.
   * @return {Boolean}
   */
  isHiddenLibraryTagStyle(blockGuid: string): boolean;
  /**
   * Returns a map of id -> className
   * @return {Object}
   */
  getClassNameMap(): Partial<Record<StyleBlock['_id'], string>>;
  /**
   * If true, this means the state is currently changing ephemerally - that is, the changes don't need to be persisted to the server.
   */
  isInEphemeralState(): boolean;
  /**
   * Depth-first traverse an array of style blocks with the given callback.
   * @param   {StyleBlockRecord[]}  styleBlocks            Either a style block or an array of style blocks to traverse through in order.
   * @param   {Function}            callback               The callback to call for each style block in the tree.
   */
  walkStyleBlockTree(
    styleBlocks: List<StyleBlock> | Array<StyleBlock>,
    callback: (arg1: StyleBlock) => any
  ): void;
  /**
   * Returns whether the given style block is used anywhere in our current view of the state.
   * @param   {StyleBlockRecord}  styleBlock              The style block to check for orphanness.
   * @param   {PageID}            currentPageId           The page ID corresponding to the current component map
   * @param   {IXState}           ixState                 The current state of IXStore
   * @return  {Boolean}                                   `true` if the style block is unused, `false` otherwise.
   */
  isRemovableStyleBlock(
    styleBlock: StyleBlock | null | undefined,
    componentMap: Component.ComponentMap,
    plugins: Plugins.PluginsType,
    currentPageId: string,
    ixState: Record<any, any>,
    ix2State: Record<any, any>,
    ix3State: IX3State,
    librariesCapabiltiesMap: LibrariesCapabilitiesMap
  ): boolean;
  /**
   * Decorates a style block by recursively resolving its children.
   */
  decorateStyleBlockChildren(styleBlock: StyleBlock): StyleBlock;
  /**
   * Exports the entire state to a mongo-compatible format.
   * @param  {componentMap}       options.componentMap Needed to compute affected counts of styleblocks on the page.
   * @param  {plugins}            options.plugins Needed to compute affected counts of styleblocks on the page.
   * @param  {PageID}             options.currentPageId ID of the current page (corresponding to the tree represented by `options.componentMap`)
   * @param  {Object}             options.slugMap A map of plugin ID -> slug
   * @return {Object[]}
   */
  exportStyleBlocks(params?: {
    componentMap: Component.ComponentMap;
    plugins: Plugins.PluginsType;
    currentPageId: string;
    slugMap: Plugins.PluginsType['slugMap'];
    variantDataMap: VariantDataMap;
    exportAllPageStyles?: boolean;
    hashContentSelectorsForPublishingCss?: boolean;
  }): Array<RawStyleBlockServerType>;
}

export type StyleBlockStateType = StyleBlockStateShape &
  StyleBlockStateMethods &
  RecordOf<StyleBlockStateShape>;
